import {
  createContext,
  Dispatch,
  FunctionComponent,
  PropsWithChildren,
  useContext,
  useReducer,
} from 'react'
import React from 'react'

import { Proposal, ProposalAdjustmentRequest } from '../../../../domain'
import { hasChanges } from '../../../../hooks/adjustments/proposal/helpers'
import { useApiErrorHandlingAsyncCallback } from '../../../../hooks/auth'
import { usePrincipal } from '../../../authentication'
import { getDraftChanges } from './getDraftChanges'
import { ProposalProviderActions, reducer } from './reducer'

interface ProposalContextObj {
  proposal: Proposal
  refresh: () => void
  dispatch: Dispatch<ProposalProviderActions>
  commit: (immediate?: ProposalAdjustmentRequest) => Promise<Proposal>
  loading: boolean
}

const ProposalContext = createContext<ProposalContextObj | undefined>(undefined)

export interface ProposalProviderProps extends PropsWithChildren {
  proposal: Proposal
}

export const ProposalProvider: FunctionComponent<ProposalProviderProps> = ({
  proposal,
  children,
}) => {
  const { apiClient } = usePrincipal()

  const [draftProposal, dispatch] = useReducer(
    reducer,
    reducer(proposal, {
      type: 'UPDATE_PROPOSAL',
      payload: proposal,
    }),
  )

  const { execute: refresh, loading: refreshLoading } = useApiErrorHandlingAsyncCallback(
    async () => {
      dispatch({
        type: 'UPDATE_PROPOSAL',
        payload: await apiClient.getProposal(proposal.proposalCode),
      })
    },
  )

  const { execute: commit, loading: commitLoading } = useApiErrorHandlingAsyncCallback(
    async (
      immediate: ProposalAdjustmentRequest = { changes: [], applyRating: false },
    ) => {
      const freshProposal = reducer(draftProposal, {
        type: 'UPDATE_PROPOSAL',
        payload: await apiClient.getProposal(draftProposal.proposalCode),
      })

      const draftChanges = getDraftChanges(freshProposal, draftProposal)

      const request: ProposalAdjustmentRequest = {
        ...immediate,
        changes: [...draftChanges, ...immediate.changes],
      }

      if (hasChanges(request)) {
        const payload = await apiClient.updateProposal(
          draftProposal.proposalCode,
          request,
        )

        dispatch({
          type: 'UPDATE_PROPOSAL',
          payload,
        })

        return payload
      }

      return draftProposal
    },
  )

  return (
    <ProposalContext.Provider
      value={{
        loading: refreshLoading || commitLoading,
        proposal: draftProposal,
        dispatch,
        refresh,
        commit,
      }}
    >
      {children}
    </ProposalContext.Provider>
  )
}

export const useProposal = (): ProposalContextObj => {
  const context = useContext(ProposalContext)
  if (!context) {
    throw new Error('Cannot use ProposalContext when undefined.')
  }
  return context
}

export const useMaybeProposal = (): Proposal | undefined =>
  useContext(ProposalContext)?.proposal
